import Cal, { getCalApi } from '@calcom/embed-react';
import { memo, useEffect, useMemo } from 'react';

import type { SchedulingHandoff } from '../lib/api';

/**
 * Inline Cal.com booking/reschedule embed shared by the applicant portal and the staff
 * appointments panel, so booking on an applicant's behalf renders identically to the applicant's
 * own booking screen (same theme, same layout, same booking page underneath).
 */
function CalcomEmbedInner({
  mapping,
}: {
  mapping: SchedulingHandoff['eventMappings'][number];
}) {
  const { origin, calLink, config } = useMemo(() => {
    const target = new URL(mapping.embedUrl);
    return {
      origin: target.origin,
      calLink: target.pathname.replace(/^\//, ''),
      config: Object.fromEntries(target.searchParams.entries()),
    };
  }, [mapping.embedUrl]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const cal = await getCalApi({ namespace: mapping.appointmentTypeId });
      if (cancelled) return;
      cal('ui', {
        theme: 'light',
        layout: 'month_view',
        styles: { branding: { brandColor: '#950001' }, body: { background: '#fffbf7' } },
        cssVarsPerTheme: {
          light: { 'cal-brand-color': '#950001' },
          dark: { 'cal-brand-color': '#950001' },
        },
      });
    })();
    return () => {
      cancelled = true;
    };
  }, [mapping.appointmentTypeId]);

  return (
    <div
      className="overflow-hidden rounded-card border border-border bg-canvas shadow-card"
      style={{ height: 'clamp(600px, 88vh, 900px)' }}
    >
      <Cal
        calOrigin={origin}
        calLink={calLink}
        namespace={mapping.appointmentTypeId}
        config={config}
        style={{ width: '100%', height: '100%', overflow: 'auto' }}
      />
    </div>
  );
}

export const CalcomEmbed = memo(CalcomEmbedInner);
